import { useMemo, useState } from "react";
import { IntlShape, useIntl } from "react-intl";

import { ImpactCategory } from "../../../__generated__/globalTypes";
import { getSmartMaxY } from "../../../util/getSmartMaxY";
import {
  complexUnitToIntlString,
  ConvertableUnit,
  convertableUnits,
  normalizeUnitsForChart,
  OtherUnit,
  otherUnits,
  Unit,
} from "../../../util/units";
import BarChart2, { Bar } from "../../graphs/BarChart2";
import { midGreyCol } from "../../graphs/colors";
import ChevronButton from "../../utils/ChevronButton";
import { InformationOutlined } from "../../utils/Vectors";
import { useImpactCategory } from "../ImpactCategoryProvider";

import "./SalesImpactByTagsBarChart.css";

const AVERAGE_DATA_MAP = {
  GHG: "total_ghg_emissions",
  LAND_USE: "total_land_use",
  WATER_USE: "total_water_use",
};

const BAR_PER_PAGE = 10;

interface DataPoint {
  raw: number;
  label: string;
  dataIndex: number;
  dataset: {
    formattedBars: Array<{
      label: string;
      percentageOfTotal: number;
      productsCount: number;
      sales: number;
      value: number[];
    }>;
  };
}

const SalesImpactByTagsBarChart = ({
  tags,
  unit,
}: {
  tags: any;
  unit: Unit[];
}) => {
  const { impactCategory } = useImpactCategory();
  const [currentChartSlide, setCurrentChartSlide] = useState(0);

  const intl = useIntl();

  const total = useMemo(() => {
    return tags.reduce(
      (accumulator: number, tag: any) =>
        accumulator + tag[AVERAGE_DATA_MAP[impactCategory]],
      0
    );
  }, [impactCategory, tags]);
  const updatedValues = normalizeUnitsForChart(
    tags.map((tag: any, id: number) => {
      return {
        value: tag[AVERAGE_DATA_MAP[impactCategory]],
        unit: unit?.find((i) =>
          convertableUnits?.includes(i as ConvertableUnit)
        ),
        meta: {
          label: tag.name,
          sales: tag.total_sales,
          productsCount: tag.product_count,
          percentageOfTotal: tag[AVERAGE_DATA_MAP[impactCategory]] / total,
        },
      };
    }),
    "max"
  );

  const bars: Array<Bar> = updatedValues.values.map((tag: any) => {
    return {
      label: tag.meta.label,
      value: [tag.convertedValue],
      sales: tag.meta.sales,
      productsCount: tag.meta.productsCount,
      percentageOfTotal: tag.meta.percentageOfTotal,
    };
  });

  const numOfSlides = useMemo(() => {
    return Math.ceil(bars.length / BAR_PER_PAGE);
  }, [bars?.length]);

  const convertedUnit = complexUnitToIntlString(
    intl,
    unit?.find((i) => otherUnits?.includes(i as OtherUnit))
      ? [
          updatedValues.displayUnit,
          unit?.find((i) => otherUnits?.includes(i as OtherUnit)) as OtherUnit,
        ]
      : [updatedValues.displayUnit]
  );

  const dependentAxisLabel = useMemo(() => {
    if (impactCategory === "GHG") {
      return intl.formatMessage(
        {
          id: "components/sales-dashboard/SalesImpactByTagsBarChart/dependentAxisLabel:ghg",
          defaultMessage: "GHG Emissions ({unit})",
        },
        { unit: convertedUnit }
      );
    }
    if (impactCategory === "LAND_USE") {
      return intl.formatMessage(
        {
          id: "components/sales-dashboard/SalesImpactByTagsBarChart/dependentAxisLabel:landUse",
          defaultMessage: "Land Use ({unit})",
        },
        { unit: convertedUnit }
      );
    }
    if (impactCategory === "WATER_USE") {
      return intl.formatMessage(
        {
          id: "components/sales-dashboard/SalesImpactByTagsBarChart/dependentAxisLabel:waterUse",
          defaultMessage: "Water Use ({unit})",
        },
        { unit: convertedUnit }
      );
    }
    return "";
  }, [convertedUnit, impactCategory, intl]);

  const maxY = useMemo(() => {
    const maxVal = Math.max(
      ...bars.flatMap((bar) =>
        bar.value.filter((v): v is number => typeof v === "number")
      )
    );
    return getSmartMaxY(maxVal);
  }, [bars]);

  const TITLE = useMemo(() => {
    if (impactCategory === "GHG") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/title:ghg",
        defaultMessage: "GHG Emissions by Tag",
      });
    }
    if (impactCategory === "LAND_USE") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/title:landUse",
        defaultMessage: "Land Use by Tag",
      });
    }
    if (impactCategory === "WATER_USE") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/title:waterUse",
        defaultMessage: "Water Use by Tag",
      });
    }
  }, [impactCategory, intl]);

  return (
    <div>
      <h4>{TITLE}</h4>
      <div style={{ marginTop: "16px" }}>
        <BarChart2
          bars={bars}
          dependentAxisLabel={dependentAxisLabel}
          grace="5%"
          height={300}
          sorted
          maintainAspectRatio={false}
          customTooltip={(tooltip: { dataPoints: Array<DataPoint> }) => (
            <Tooltip
              dataPoints={tooltip.dataPoints}
              impactCategory={impactCategory}
              intl={intl}
              unit={convertedUnit}
            />
          )}
          hideOtherBarsOnHover
          minIndex={currentChartSlide * BAR_PER_PAGE}
          maxIndex={(currentChartSlide + 1) * BAR_PER_PAGE - 1}
          maxY={maxY}
          optionsAdditionalProps={{
            animation: {
              duration: 300,
              easing: "easeInOutQuad",
            },
          }}
          xTicksExtraProps={{
            autoSkip: false,
            maxRotation: 30,
          }}
          truncateLabels
        />
      </div>
      <div className="SalesImpactByTagsBarChart_controls_comment_section">
        <div className="small-graph-legends SalesImpactByTagsBarChart_comment">
          <InformationOutlined fill={midGreyCol} width={16} />
          {intl.formatMessage({
            id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/comment",
            defaultMessage: "Products can belong to multiple tags",
          })}
        </div>
        {numOfSlides > 1 && (
          <div className="Carousel_ChevronButtons">
            <ChevronButton
              disabled={currentChartSlide === 0}
              direction="back"
              onClick={() =>
                currentChartSlide !== 0
                  ? setCurrentChartSlide((prev) => prev - 1)
                  : null
              }
            />
            <ChevronButton
              disabled={numOfSlides - 1 === currentChartSlide}
              direction="forward"
              onClick={() =>
                numOfSlides - 1 === currentChartSlide
                  ? null
                  : setCurrentChartSlide((prev) => prev + 1)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesImpactByTagsBarChart;

interface TooltipProps {
  dataPoints: Array<DataPoint>;
  impactCategory: ImpactCategory;
  intl: IntlShape;
  unit: string;
}

function Tooltip(props: TooltipProps) {
  const { dataPoints, intl, unit } = props;

  const data = dataPoints[0].dataset.formattedBars[dataPoints[0].dataIndex];

  return (
    <div className="SalesImpactByTagsBarChart_Tooltip">
      <div className="body-copy-medium">{data.label}</div>
      <div className="d-flex" style={{ gap: 24 }}>
        <div className="SalesImpactByTagsBarChart_column">
          <div className="SalesImpactByTagsBarChart_label_value_container">
            <span className="medium-font SalesImpactByTagsBarChart_value">
              {intl.formatNumber(data.value[0], { maximumFractionDigits: 2 })}
            </span>
            <span className="small-graph-legends SalesImpactByTagsBarChart_description">
              {unit}
            </span>
          </div>
          <div className="SalesImpactByTagsBarChart_label_value_container">
            <span className="medium-font SalesImpactByTagsBarChart_value">
              {intl.formatNumber(data.productsCount)}
            </span>
            <span className="small-graph-legends SalesImpactByTagsBarChart_description">
              {intl.formatMessage({
                id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/tooltip/products",
                defaultMessage: "products",
              })}
            </span>
          </div>
        </div>
        <div className="SalesImpactByTagsBarChart_column">
          <div className="SalesImpactByTagsBarChart_label_value_container">
            <span className="medium-font SalesImpactByTagsBarChart_value">
              {intl.formatNumber(data.percentageOfTotal, {
                maximumFractionDigits: 2,
                style: "percent",
              })}
            </span>
            <span className="small-graph-legends SalesImpactByTagsBarChart_description">
              {intl.formatMessage({
                id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/tooltip/ofTotal",
                defaultMessage: "of total",
              })}
            </span>
          </div>
          <div className="SalesImpactByTagsBarChart_label_value_container">
            <span className="medium-font SalesImpactByTagsBarChart_value">
              {intl.formatNumber(data.sales)}
            </span>
            <span className="small-graph-legends SalesImpactByTagsBarChart_description">
              {intl.formatMessage({
                id: "components/pages:SalesDashBoard/SalesImpactByTagsBarChart/tooltip/sales",
                defaultMessage: "sales",
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
